/**
 * Record inspector data fetching.
 *
 * Calls bp-api directly: GET /bp/v1/inspect/rfp/:rfpId and
 * GET /bp/v1/inspect/client/:clientId, admin-only (ActionInspectRead).
 */

import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { useApi } from "@platform/api";

import type { AxiosInstance } from "axios";

/** The full stored record graph, field names as stored — deliberately untyped. */
export type InspectorRecord = Record<string, unknown>;

const fetchInspectRfp = async (
  rfpId: number,
  axiosInstance: AxiosInstance,
  signal?: AbortSignal,
): Promise<InspectorRecord> => {
  const response = await axiosInstance.get<InspectorRecord>(
    `/bp/v1/inspect/rfp/${rfpId}`,
    { signal },
  );

  return response.data;
};

const fetchInspectClient = async (
  clientId: number,
  axiosInstance: AxiosInstance,
  signal?: AbortSignal,
): Promise<InspectorRecord> => {
  const response = await axiosInstance.get<InspectorRecord>(
    `/bp/v1/inspect/client/${clientId}`,
    { signal },
  );

  return response.data;
};

export const useInspectRfp = (
  rfpId: number,
  enabled: boolean,
): UseQueryResult<InspectorRecord, Error> => {
  const axiosInstance = useApi();

  return useQuery({
    queryKey: ["inspect-rfp", rfpId],
    queryFn: ({ signal }) => fetchInspectRfp(rfpId, axiosInstance, signal),
    enabled,
  });
};

export const useInspectClient = (
  clientId: number,
  enabled: boolean,
): UseQueryResult<InspectorRecord, Error> => {
  const axiosInstance = useApi();

  return useQuery({
    queryKey: ["inspect-client", clientId],
    queryFn: ({ signal }) =>
      fetchInspectClient(clientId, axiosInstance, signal),
    enabled,
  });
};
